// chatReducer.js
import { chatConstants, messageConstants } from "../actions/constants";

const initialState = {
    chats: [],
    loading: false,
    activeChatId: null,
    error: null,
    handledMessageIds: {},
};

const getId = (value) => String(value?._id || value || "");

const getSenderId = (message) => {
    return getId(message?.sender);
};

const getChatId = (message) => {
    return getId(message?.chatId);
};

const getMessagePreviewObject = (message) => ({
    _id: message._id,
    type: "message",
    messageKind: message.messageKind || "text",
    entity: message.entity || { type: "none" },
    text: message.text || "",
    fileName: message.fileName || null,
    fileUrl: message.fileUrl || null,
    fileType: message.fileType || null,
    fileMime: message.fileMime || null,
    sender: message.sender,
    senderId: getSenderId(message),
    createdAt: message.createdAt,
});

const trimHandled = (map = {}) => {
    const entries = Object.entries(map);

    if (entries.length <= 700) return map;

    return Object.fromEntries(entries.slice(entries.length - 700));
};

const upsertChat = (chats = [], chat) => {
    if (!chat?._id) return chats;

    const exists = chats.some((item) => String(item._id) === String(chat._id));

    const next = exists
        ? chats.map((item) => String(item._id) === String(chat._id) ? { ...item, ...chat } : item)
        : [chat, ...chats];

    next.sort((a, b) => (
        new Date(b.lastMessageAt || b.updatedAt || b.createdAt || 0) -
        new Date(a.lastMessageAt || a.updatedAt || a.createdAt || 0)
    ));

    return next;
};

export default function chatReducer(state = initialState, action) {
    switch (action.type) {
        case chatConstants.FETCH_CHATS_REQUEST:
        case chatConstants.FETCH_MY_CHATS_REQUEST:
        case chatConstants.CREATE_CHAT_REQUEST:
        case chatConstants.CREATE_DM_REQUEST:
        case chatConstants.CREATE_ROOM_REQUEST:
            return {
                ...state,
                loading: true,
                error: null,
            };

        case chatConstants.FETCH_CHATS_SUCCESS:
        case chatConstants.FETCH_MY_CHATS_SUCCESS:
            return {
                ...state,
                chats: action.payload?.replace === false
                    ? (action.payload?.chats || []).reduce((acc, chat) => upsertChat(acc, chat), state.chats)
                    : action.payload?.chats || [],
                loading: false,
                error: null,
            };

        case chatConstants.CREATE_CHAT_SUCCESS:
        case chatConstants.CREATE_DM_SUCCESS:
        case chatConstants.CREATE_ROOM_SUCCESS:
        case chatConstants.UPSERT_CHAT: {
            const chat = action.payload?.chat;

            if (!chat?._id) {
                return {
                    ...state,
                    loading: false,
                };
            }

            return {
                ...state,
                loading: false,
                error: null,
                chats: upsertChat(state.chats, chat),
                activeChatId: action.type === chatConstants.UPSERT_CHAT
                    ? state.activeChatId
                    : chat._id,
            };
        }

        case chatConstants.REMOVE_CHAT: {
            const chatId = getId(action.payload?.chatId || action.payload);

            if (!chatId) return state;

            return {
                ...state,
                chats: state.chats.filter((chat) => String(chat._id) !== chatId),
                activeChatId: String(state.activeChatId || "") === chatId ? null : state.activeChatId,
            };
        }

        case chatConstants.FETCH_CHATS_FAILURE:
        case chatConstants.FETCH_MY_CHATS_FAILURE:
        case chatConstants.CREATE_CHAT_FAILURE:
        case chatConstants.CREATE_DM_FAILURE:
        case chatConstants.CREATE_ROOM_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.payload?.error || "Ошибка загрузки чатов",
            };

        case chatConstants.SET_ACTIVE_CHAT_ID:
            return {
                ...state,
                activeChatId: action.payload?.chatId || null,
            };

        case messageConstants.RECEIVE_NEW_MESSAGE:
        case messageConstants.SEND_MESSAGE_SUCCESS:
        case messageConstants.SEND_TEXT_SUCCESS:
        case messageConstants.SEND_FILE_SUCCESS:
        case messageConstants.SEND_VOICE_SUCCESS: {
            const message = action.payload?.message;

            if (!message?.chatId || message?.__starPatch) return state;

            const chatId = getChatId(message);
            const messageId = getId(message._id);
            const meId = getId(action.payload?.meId);
            const mine = meId && getSenderId(message) === meId;
            const isActiveChat = String(state.activeChatId || "") === chatId;
            const isRealtime = action.type === messageConstants.RECEIVE_NEW_MESSAGE;
            const alreadyHandled = messageId ? !!state.handledMessageIds[messageId] : false;

            let found = false;

            const chats = state.chats.map((chat) => {
                if (String(chat._id) !== chatId) return chat;

                found = true;

                const unreadCount = Number(chat.unreadCount || 0);
                const lastMessageId = getId(chat.lastMessage?._id);
                const sameAsLast = messageId && lastMessageId === messageId;
                const shouldIncrementUnread = isRealtime && !alreadyHandled && !sameAsLast && !mine && !isActiveChat;

                return {
                    ...chat,
                    lastMessage: getMessagePreviewObject(message),
                    lastMessageAt: message.createdAt || new Date().toISOString(),
                    updatedAt: message.createdAt || new Date().toISOString(),
                    unreadCount: shouldIncrementUnread ? unreadCount + 1 : unreadCount,
                };
            });

            if (!found) return state;

            chats.sort((a, b) => (
                new Date(b.lastMessageAt || b.updatedAt || b.createdAt || 0) -
                new Date(a.lastMessageAt || a.updatedAt || a.createdAt || 0)
            ));

            return {
                ...state,
                chats,
                handledMessageIds: messageId
                    ? trimHandled({
                        ...state.handledMessageIds,
                        [messageId]: true,
                    })
                    : state.handledMessageIds,
            };
        }

        case messageConstants.MARK_MESSAGES_AS_READ_SUCCESS: {
            const chatId = getId(action.payload?.chatId);

            if (!chatId) return state;

            return {
                ...state,
                chats: state.chats.map((chat) => (
                    String(chat._id) === chatId
                        ? { ...chat, unreadCount: 0 }
                        : chat
                )),
            };
        }

        default:
            return state;
    }
}